import { useEffect, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Building2, Globe, Scale } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBusinessUnit } from "@/hooks/useBusinessUnit";
import { useHouse } from "@/hooks/useHouses";
import { BusinessUnitIcon } from "@/components/business-unit/BusinessUnitIcon";
import { Sidebar, type AdminArea } from "@/components/layout/Sidebar";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { UserDropdown } from "@/components/layout/UserDropdown";
import {
  ADMIN_HEADER_HEIGHT_PX,
  useAdminStickyTop,
} from "@/components/layout/admin/adminChrome";
import { cn } from "@/utils/cn";
import { scopeLabel } from "@/utils/scopeLabel";

/**
 * Shared admin shell: the role Sidebar plus a content column with a top bar
 * (context chip on the left; language switch, bell and portal-switcher avatar
 * on the right),
 * rather than the end-user top navbar. The global Navbar is suppressed in
 * admin areas (see routes/__root.tsx).
 *
 * The bell is repeated here rather than left to the Navbar: suppressing the
 * Navbar would otherwise remove the only notification surface, leaving an
 * admin's own notifications — the ones the transactional emails tell them to
 * act on — reachable only by switching to the end-user portal.
 *
 * The area (which sidebar/chip to show) is derived from the route prefix, not
 * the viewer's role, so a super admin browsing /bu-admin sees the BU-admin
 * rail.
 *
 * Everything pinned here sits below the banners the root pins at the top of
 * every page (`bannerStackHeightPx`, Jira NAV-222), so "User view" at the foot of
 * the rail is on screen and no banner covers the top bar, however many
 * banners are showing.
 */

interface ChipConfig {
  icon: LucideIcon;
  className: string;
}

/** The icon a chip falls back to when it is **not** naming one unit — the
 *  group as a whole, or the group's legal function. A chip that names a unit
 *  draws that unit's own icon instead (NAV-116): a fixed icon per admin area
 *  was the whole of "the icon beside the unit's name never changes", because it
 *  is per area and the picker is per unit. */
const CHIP_BY_AREA: Record<AdminArea, ChipConfig> = {
  super_admin: { icon: Globe, className: "bg-hb-navy-dark text-white" },
  bu_admin: {
    icon: Building2,
    className: "bg-muted text-hb-navy-dark dark:text-white",
  },
  group_legal: { icon: Scale, className: "bg-emerald-700 text-white" },
};

function areaFromPath(pathname: string): AdminArea {
  if (pathname.startsWith("/bu-admin")) return "bu_admin";
  if (pathname.startsWith("/group-legal")) return "group_legal";
  return "super_admin";
}

export function AdminLayout({ children }: { children: ReactNode }) {
  const { businessUnitId, houseId } = useAuth();
  const { businessUnit } = useBusinessUnit(businessUnitId);
  const { house } = useHouse(businessUnitId, houseId);
  const { t } = useTranslation(["admin", "groupLegal"]);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const area = areaFromPath(pathname);

  const chip = CHIP_BY_AREA[area];
  const ChipIcon = chip.icon;
  const unitName = businessUnit?.name ?? null;
  /**
   * What this reader is administering, which for a house's admin is the house
   * and not the unit (ADR 0025 decision 4).
   *
   * "Rowohlt · Holtzbrinck Buchverlage", the same pairing the group's reviewer
   * sees on a house's conclusion, because both halves are load-bearing: the
   * house is the scope of everything on these pages, and the unit is what
   * fixes which Rowohlt it is — a house id is unique only inside its unit.
   * Naming only the unit was the bug: a house admin was shown the name of the
   * thing whose settings, contracts and policies the rules refuse them.
   *
   * The house's name is what waits, not the unit's: until it arrives the chip
   * shows the unit alone rather than a house called `rowohlt`, since a raw id
   * is worse than a true statement about where they are.
   */
  const scopeName =
    unitName === null
      ? null
      : scopeLabel(unitName, houseId ? house?.name : null);
  const chipLabel =
    area === "group_legal"
      ? t("groupLegal:chip")
      : area === "super_admin"
        ? (unitName ?? t("admin:groupChip"))
        : (scopeName ?? "");
  // Whether the chip is naming a *unit*, which is the only case where the
  // unit's own icon is the right picture. Group legal's chip names a function
  // and the super admin's falls back to naming the group, and neither of those
  // has an icon a unit admin picked.
  const namesUnit = area !== "group_legal" && unitName !== null;
  const { banners: bannersPx, belowTopBar } = useAdminStickyTop();
  // Keyboard focus and in-page scrolling stop below the pinned banners and top
  // bar rather than behind them (WCAG 2.4.11, focus not obscured): the window
  // scrolls beneath both, so an element scrolled into view at the top edge
  // would otherwise land under them.
  useEffect(() => {
    const root = document.documentElement;
    root.style.scrollPaddingTop = `${belowTopBar}px`;
    return () => {
      root.style.scrollPaddingTop = "";
    };
  }, [belowTopBar]);

  return (
    <div
      className="flex bg-background"
      style={{ minHeight: `calc(100dvh - ${bannersPx}px)` }}
    >
      <Sidebar area={area} top={bannersPx} />
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Pinned below the banners while the page scrolls (Jira NAV-28,
            Jira NAV-222), and the same height as the rail's own header so the two
            read as one strip across the screen. Opaque for the reason the
            member navbar is: content scrolls under it. */}
        <header
          className="sticky z-40 flex shrink-0 items-center justify-between border-b border-border bg-background px-6"
          style={{ top: bannersPx, height: ADMIN_HEADER_HEIGHT_PX }}
        >
          {chipLabel ? (
            <div
              className={cn(
                "flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium",
                chip.className,
              )}
              data-testid="admin-context-chip"
            >
              {namesUnit ? (
                <BusinessUnitIcon
                  icon={businessUnit?.icon}
                  className="h-4 w-4"
                />
              ) : (
                <ChipIcon className="h-4 w-4" />
              )}
              <span>{chipLabel}</span>
            </div>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <NotificationBell />
            <UserDropdown />
          </div>
        </header>
        {/* No `overflow-y-auto` here. It would declare a scrollport that never
            scrolls — the column grows with its content and the window is what
            actually scrolls — while still being the containing scrollport for
            anything `position: sticky` inside it, which would pin such an
            element to a scroll position that never moves. The request
            detail's summary bar (Jira NAV-64) is the page that needs it. */}
        <main className="flex-1 p-8">{children}</main>
      </div>
    </div>
  );
}
